import PropTypes from 'prop-types';
import React from 'react';

import { LocaleService, ContentZonesService, WindowProxy } from '@sm360/widget-commons-core-services';
import { ModalService, LightBox, AnalyticsService, TRACKING_TAGS as TAGS } from '@sm360/widget-commons-general';

import VehicleModelV25 from 'commons-vehicle/Vehicle/models/VehicleModelV25.js';

import ExpandPhoto from 'commons-vehicle/Vehicle/components/VehicleBannerWithCarrousel/ExpandPhoto/ExpandPhoto.jsx';
import Photos from 'commons-vehicle/Vehicle/components/VehicleBannerWithCarrousel/Photos/Photos.jsx';
import VideoWrapper from 'commons-vehicle/Vehicle/components/VehicleBannerWithCarrousel/Video/VideoWrapper.jsx';
import VehicleBannerOverlay from 'commons-vehicle/Vehicle/components/VehicleBannerWithCarrousel/VehicleBanner/VehicleBannerOverlay/VehicleBannerOverlay.jsx';

import 'commons-vehicle/Vehicle/components/VehicleBannerWithCarrousel/VehicleBanner/VehicleBanner.scss';

class VehicleBanner extends React.Component {
    constructor(props) {
        super(props);

        this.modal = ModalService.get(this.constructor.MODAL);

        this.state = {
            lightBoxIsOpen: false,
            lightBoxStartIndex: 0,
        };

        this.toggleLightBox = this.toggleLightBox.bind(this);
        this.handleClickEvent = this.handleClickEvent.bind(this);
        this.handleMobileClickEvent = this.handleMobileClickEvent.bind(this);
        this.showVideoModal = this.showVideoModal.bind(this);
    }

    showVideoModal() {
        this.modal.open();
    }

    handleClickEvent(event) {
        event.preventDefault();
        const locale = LocaleService.getSimpleLocale();
        if (this.props.vehicle.multimedia.hasVideo(locale)) {
            this.showVideoModal();
        } else {
            this.toggleLightBox();
        }
    }

    handleMobileClickEvent(event) {
        event.preventDefault();
        event.stopPropagation();

        this.toggleLightBox();
    }

    toggleLightBox(index) {
        if (this.props.vehicle.multimedia.hasPictures()) {
            this.setState((previousState) => {
                return {
                    lightBoxIsOpen: !previousState.lightBoxIsOpen,
                    lightBoxStartIndex: !previousState.lightBoxIsOpen && index ? index : 0,
                };
            });
        }
    }

    render() {
        const { loading } = this.props;
        if (loading) {
            return null;
        }
        const locale = LocaleService.getSimpleLocale();
        const vehicleHasPictures = this.props.vehicle.multimedia.hasPictures();
        const vehicleVideo = this.props.vehicle.multimedia.getVideo(locale);

        const isLocked = this.props.vehicle.onHold && (
            <VehicleBannerOverlay icon="icon-lock" content={LocaleService.t('VEHICLE_STATUS.IS_LOCKED')} />
        );

        const isSold = this.props.vehicle?.isSold() && (
            <VehicleBannerOverlay icon="icon-lock" content={LocaleService.t('VEHICLE_STATUS.IS_SOLD')} />
        );

        const isComingSoon = this.props.vehicle?.comingSoon;

        const isComingSoonFilter = isComingSoon &&
            !this.props.vehicle.locked && (
                <VehicleBannerOverlay icon="icon-stopwatch" content={LocaleService.t('VEHICLE_STATUS.COMING_SOON')} />
            );

        const personsViewed = this.props.vehicle.pageViewSummary.recentViewCount > 0 && (
            <div className="vehicleBanner-personsViewed">
                <icon className="icon-eye picto-showroom" />
                <span className="amount is-price">{this.props.vehicle.pageViewSummary.recentViewCount}</span>
                <span className="label">
                    {LocaleService.t(
                        this.props.vehicle.pageViewSummary.recentViewCount === 1
                            ? 'VDP_BANNER_PERSON_LOOKING'
                            : 'VDP_BANNER_PERSONS_LOOKING'
                    )}
                </span>
            </div>
        );

        const contentZoneDefaultPicture = ContentZonesService.getContentZone(
            'widget.vdp.used.similar.cars',
            'default.picture'
        );

        let imageUrl = '';
        if (this.props.vehicle && this.props.vehicle.vehicleId) {
            imageUrl = this.props.vehicle.multimedia.getMainImage() || contentZoneDefaultPicture.getValue() || '';
        }

        const yearMakeModel = this.props.vehicle.getYearMakeModel();

        let thumbnail = <img src={imageUrl} alt={yearMakeModel} />;
        if (vehicleVideo) {
            thumbnail = (
                <div>
                    <icon className="icon-play-button vehicleBanner-playVideoThumbnail-button" />
                    <img src={imageUrl} alt={yearMakeModel} />
                </div>
            );
        }

        const expandPhoto = !vehicleVideo &&
            vehicleHasPictures && <ExpandPhoto togglePhotoLightBox={this.toggleLightBox} />;

        const wrapperClasses = ['vehicleBanner--image-wrapper'];
        if (vehicleHasPictures || vehicleVideo) {
            wrapperClasses.push('is-clickable');
        }

        const photos = vehicleHasPictures && (
            <Photos
                video={vehicleVideo}
                pictures={this.props.vehicle.multimedia.pictures}
                togglePhotoLightBox={this.toggleLightBox}
                toggleVideoModal={this.showVideoModal}
            />
        );

        const carouselBtn = vehicleHasPictures && (
            <div className="carouselBtn sr-button-1 is-small" onClick={this.handleMobileClickEvent}>
                <icon className="icon-gallery" />
                {this.props.vehicle.multimedia.pictures.length}
            </div>
        );

        const trackLightboxNextBtn = () =>
            AnalyticsService.gaTrigger(TAGS.USED_VDP_PICTURES__NEXT(WindowProxy.location().pathname));

        return (
            <div className={wrapperClasses.join(' ')}>
                <div className="vehicleBanner--image" onClick={this.handleClickEvent}>
                    {isSold || isLocked}
                    {isComingSoonFilter}
                    {carouselBtn}
                    {thumbnail}
                </div>
                {expandPhoto}
                {photos}
                {personsViewed}

                {vehicleVideo && <VideoWrapper videoURL={vehicleVideo} />}

                {this.state.lightBoxIsOpen && (
                    <LightBox
                        pictures={this.props.vehicle.multimedia.pictures}
                        toggleLightBox={this.toggleLightBox}
                        imageIndex={this.state.lightBoxStartIndex}
                        onGoToNext={trackLightboxNextBtn}
                    />
                )}
            </div>
        );
    }
}

VehicleBanner.MODAL = 'VehicleBanner/VehicleVideo';

VehicleBanner.propTypes = {
    vehicle: PropTypes.instanceOf(VehicleModelV25).isRequired,
    loading: PropTypes.bool.isRequired,
};

VehicleBanner.displayName = 'VehicleBanner';

export default VehicleBanner;
